import { useState } from 'react';

type OptionData = {
    id?: string | number | null;
    name?: string | null;
};

interface Props {
    isShow: boolean;
    currentProposalType?: OptionData | null;
    proposedProposalType?: OptionData | null;
    currentPrototypeType?: OptionData | null;
    proposedPrototypeType?: OptionData | null;
    currentProductType?: OptionData | null;
    proposedProductType?: OptionData | null;
    currentBasicPlan?: OptionData | null;
    proposedBasicPlan?: OptionData | null;
    currentIsPrototype: boolean;
    proposedIsPrototype: boolean;
    currentIsCustomize: boolean;
    proposedIsCustomize: boolean;
}

type ChangeItem = {
    key: string;
    label: string;
    current: string;
    proposed: string;
};

const normalizeValue = (value: unknown): string => {
    if (value === null || value === undefined) {
        return '';
    }

    return String(value).trim();
};

const getId = (value?: OptionData | null): string => {
    if (value?.id === null || value?.id === undefined) {
        return '';
    }

    return String(value.id);
};

const getName = (value?: OptionData | null): string => {
    return normalizeValue(value?.name);
};

const isDifferent = (
    current?: OptionData | null,
    proposed?: OptionData | null,
): boolean => {
    return getId(current) !== getId(proposed);
};

const NoChanges = () => {
    return (
        <div className="flex items-center gap-2 rounded-md bg-gray-50 px-3 py-3 text-[11px] text-gray-400">
            <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4 shrink-0" aria-hidden="true" >
                <path d="m5 12.5 4.5 4L19 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>No changes</span>
        </div>
    );
};

const ChangeItemView = ({ item }: { item: ChangeItem }) => {
    return (
        <div className="grid grid-cols-1 gap-3 border-b border-gray-100 py-3 last:border-b-0 sm:grid-cols-2">
            <div className="min-w-0">
                <div className="mb-1 text-[9px] font-semibold uppercase tracking-wide text-gray-400">
                    {item.label}
                </div>
                <div className="break-words text-[11px] leading-relaxed text-gray-700">
                    {item.current || 'Not provided'}
                </div>
            </div>
            <div className="min-w-0 rounded-md bg-[#eef6fc] px-2.5 py-2">
                <div className="mb-1 text-[9px] font-semibold uppercase tracking-wide text-[#186cb2]">
                    Proposed
                </div>
                <div className="break-words text-[11px] leading-relaxed text-gray-800">
                    {item.proposed || 'Not provided'}
                </div>
            </div>
        </div>
    );
};

export default function ProposedProposalConfiguration({
    isShow,
    currentProposalType,
    proposedProposalType,
    currentPrototypeType,
    proposedPrototypeType,
    currentProductType,
    proposedProductType,
    currentBasicPlan,
    proposedBasicPlan,
    currentIsPrototype,
    proposedIsPrototype,
    currentIsCustomize,
    proposedIsCustomize,
}: Props) {

    const [showContent, setShowContent] = useState(true);
    const changes: ChangeItem[] = [];

    // ============================================================
    // TYPE OF PROPOSAL
    // ============================================================
    if (isDifferent(currentProposalType, proposedProposalType)) {
        changes.push({
            key: 'type-of-proposal',
            label: 'Type of Proposal',
            current: getName(currentProposalType),
            proposed: getName(proposedProposalType),
        });
    }

    // ============================================================
    // TYPE OF PROTOTYPE
    // ============================================================
    const prototypeChanged =
        currentIsPrototype !== proposedIsPrototype ||
        (currentIsPrototype && proposedIsPrototype && isDifferent(currentPrototypeType, proposedPrototypeType));

    if (prototypeChanged) {
        changes.push({
            key: 'type-of-prototype',
            label: 'Type of Prototype',
            current: currentIsPrototype ? getName(currentPrototypeType) : '',
            proposed: proposedIsPrototype ? getName(proposedPrototypeType) : '',
        });
    }

    // ============================================================
    // TYPE OF PRODUCT
    // ============================================================
    const productChanged =
        currentIsCustomize !== proposedIsCustomize ||
        (currentIsCustomize && proposedIsCustomize && isDifferent(currentProductType, proposedProductType));

    if (productChanged) {
        changes.push({
            key: 'type-of-product',
            label: 'Type of Product',
            current: currentIsCustomize ? getName(currentProductType) : '',
            proposed: proposedIsCustomize ? getName(proposedProductType) : '',
        });
    }

    // ============================================================
    // BASIC PLANS
    // ============================================================
    const basicPlanChanged =
        currentIsCustomize !== proposedIsCustomize ||
        (currentIsCustomize && proposedIsCustomize && isDifferent(currentBasicPlan, proposedBasicPlan));

    if (basicPlanChanged) {
        changes.push({
            key: 'basic-plans',
            label: 'Basic Plans',
            current: currentIsCustomize ? getName(currentBasicPlan) : '',
            proposed: proposedIsCustomize ? getName(proposedBasicPlan) : '',
        });
    }

    if (!isShow || changes.length === 0) {
        return null;
    }

    return (
        <div className="grid grid-cols-1 gap-3.5">
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                {/* ================================================= */}
                {/* SECTION HEADER */}
                {/* ================================================= */}
                <button
                    type="button"
                    onClick={() => setShowContent((previous) => !previous)}
                    className="flex w-full items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-2.5 text-left transition-colors hover:bg-slate-50"
                >
                    <div className="flex min-w-0 items-center gap-2">
                        <svg viewBox="0 0 24 24" fill="none" className={`h-3.5 w-3.5 shrink-0 text-[#186cb2] transition-transform ${showContent ? 'rotate-0' : '-rotate-90'}`} aria-hidden="true" >
                            <path d="m7 10 5 5 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                            Proposal Configuration
                        </h3>
                        <span className="rounded-full bg-[#e3f1fa] px-1.5 py-0.5 text-[8px] font-bold text-[#186cb2] ring-1 ring-[#c7e0f3]">
                            {changes.length}
                        </span>
                    </div>
                    <span className="shrink-0 text-[9px] font-medium text-slate-400">
                        {showContent ? 'Hide' : 'Show'}
                    </span>
                </button>
                {showContent && (
                    <>
                        {/* ================================================= */}
                        {/* CURRENT / PROPOSED HEADER */}
                        {/* ================================================= */}
                        <div className="grid grid-cols-1 border-b border-slate-200 sm:grid-cols-2">
                            <div className="border-b border-slate-200 bg-slate-50 px-4 py-2.5 sm:border-b-0 sm:border-r">
                                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                    Current
                                </h3>
                            </div>
                            <div className="bg-[#f5f9fc] px-4 py-2.5">
                                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-[#186cb2]">
                                    Proposed
                                </h3>
                            </div>
                        </div>
                        {/* ================================================= */}
                        {/* CONTENT */}
                        {/* ================================================= */}
                        <div className="grid grid-cols-1 sm:grid-cols-2">
                            {/* CURRENT */}
                            <div className="min-w-0 border-b border-slate-200 p-4 sm:border-b-0 sm:border-r">
                                {changes.length === 0 ? (
                                    <NoChanges />
                                ) : (
                                    <div className="space-y-3">
                                        {changes.map((item) => (
                                            <div key={`current-${item.key}`} className="border-b border-gray-100 pb-3 last:border-b-0 last:pb-0" >
                                                <div className="mb-1 text-[9px] font-semibold uppercase tracking-wide text-gray-400">
                                                    {item.label}
                                                </div>
                                                <div className="break-words text-[11px] leading-relaxed text-gray-700">
                                                    {item.current || 'Not provided'}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {/* PROPOSED */}
                            <div className="min-w-0 p-4">
                                {changes.length === 0 ? (
                                    <NoChanges />
                                ) : (
                                    <div className="space-y-3">
                                        {changes.map((item) => (
                                            <div key={`proposed-${item.key}`} className="border-b border-blue-50 pb-3 last:border-b-0 last:pb-0" >
                                                <div className="mb-1 text-[9px] font-semibold uppercase tracking-wide text-[#186cb2]">
                                                    {item.label}
                                                </div>
                                                <div className="break-words text-[11px] leading-relaxed text-gray-800">
                                                    {item.proposed || 'Not provided'}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}